"use client";

import { Car } from "lucide-react";
import Link from "next/link";
import React from "react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface GetDirectionsButtonProps
  extends React.ComponentProps<typeof Button> {
  /** The Google Maps URL for directions */
  directionsUrl: string;
  /** Custom test id */
  testId?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Whether to open in new tab */
  openInNewTab?: boolean;
  /** Tooltip text */
  tooltip?: string;
}

export default function GetDirectionsButton({
  directionsUrl,
  testId,
  variant = "outline",
  size,
  className = "rounded-md h-10 w-10 p-0",
  icon = <Car className="w-5 h-5" />,
  openInNewTab = true,
  tooltip = "Get Directions",
  children,
  ...props
}: GetDirectionsButtonProps) {
  const linkProps = openInNewTab
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link href={directionsUrl} {...linkProps}>
            <Button
              variant={variant}
              size={size}
              className={className}
              data-testid={testId}
              {...props}
            >
              {icon}
              {children}
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
